import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Factory, Server, ClipboardList, Activity, AlertOctagon, LineChart, Users,
  ArrowRight, Sparkles, ShieldCheck, Zap, Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const PILLARS = [
  { slug: "oee",         title: "OEE Dashboard",       desc: "Availability × Performance × Quality. Live plant-wide overall equipment effectiveness with daily trend.", icon: LineChart,     href: "/app/shopfloor/oee",         hero: true },
  { slug: "work-orders", title: "Work Orders",         desc: "Plan · release · start · pause · complete. Track quantities, scrap and cycle times against each order.",  icon: ClipboardList, href: "/app/shopfloor/work-orders" },
  { slug: "machines",    title: "Machines & Assets",   desc: "Real-time status of every asset — running · idle · setup · down · maintenance — with reason codes.",       icon: Server,        href: "/app/shopfloor/machines" },
  { slug: "downtime",    title: "Downtime Tracker",    desc: "Log breakdowns, changeovers, material starvation, quality holds. Pareto by cause.",                         icon: AlertOctagon,  href: "/app/shopfloor/downtime" },
  { slug: "production",  title: "Production Logs",     desc: "Shift-by-shift units produced, scrap, cycle times. Rolls up to work orders and OEE automatically.",          icon: Activity,      href: "/app/shopfloor/production" },
  { slug: "shifts",      title: "Shift Board",         desc: "Who's running what right now. Start & end shifts, assign operators to machines and work orders.",           icon: Users,         href: "/app/shopfloor/shifts" },
];

const EMPTY_CONSULT = { name: "", email: "", company: "", industry: "", machines_count: "", goals: "", role: "" };

export default function GmesHub() {
  const [summary, setSummary] = useState(null);
  const [openConsult, setOpenConsult] = useState(false);
  const [consult, setConsult] = useState(EMPTY_CONSULT);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/gmes/oee/summary?days=7").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  const submitConsult = async (e) => {
    e.preventDefault();
    if (!consult.name.trim() || !consult.email.trim()) { toast.error("Name and email required"); return; }
    setSubmitting(true);
    try {
      await api.post("/public/gmes/consult", consult);
      setSent(true);
      toast.success("Request received. Scott will reach out within 1 business day.");
    } catch (err) {
      toast.error("Something went wrong. Try again or email support@ruleai.systems.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeConsult = () => {
    setOpenConsult(false);
    setTimeout(() => { setSent(false); setConsult(EMPTY_CONSULT); }, 250);
  };

  const hero = PILLARS.find((p) => p.hero);
  const rest = PILLARS.filter((p) => !p.hero);
  const totals = summary?.totals || {};
  const avg = summary?.avg || {};

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      {/* Header */}
      <div className="mb-8">
        <div className="overline text-accent">/ OPERATIONS · SHOP FLOOR</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-3" data-testid="gmes-hub-title">
          Shop Floor Operations
        </h1>
        <p className="text-muted-foreground mt-3 max-w-3xl">
          Run the shop floor from one screen. Work orders, live machine states, downtime, production logs, shift boards and full OEE — all wired to your tenant, ready to use.
        </p>
      </div>

      {/* Live KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        {[
          { label: "OEE (7d avg)",    value: `${avg.oee_pct ?? 0}%`,          test: "kpi-oee" },
          { label: "Availability",     value: `${avg.availability_pct ?? 0}%`, test: "kpi-avail" },
          { label: "Units produced",   value: totals.units_produced ?? 0,       test: "kpi-units" },
          { label: "Downtime (min)",   value: totals.downtime_minutes ?? 0,     test: "kpi-downtime" },
        ].map((k) => (
          <div key={k.label} className="border border-border bg-card rounded-sm p-4" data-testid={k.test}>
            <div className="overline text-muted-foreground">{k.label}</div>
            <div className="font-display font-black text-3xl mt-1 tabular-nums">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Hero pillar */}
      {hero && (
        <Link to={hero.href} className="block group mb-10" data-testid="gmes-hero-oee">
          <div className="relative overflow-hidden border border-border rounded-sm bg-zinc-950 text-white p-8 lg:p-10 hover:border-accent transition-colors">
            <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 border border-accent/40 bg-accent/10 rounded-sm px-3 py-1.5 mb-4">
                  <Sparkles className="w-3.5 h-3.5 text-accent" />
                  <span className="text-[10px] font-mono-plex uppercase tracking-widest text-accent">◉ Real-time · OEE</span>
                </div>
                <div className="font-display font-black text-3xl lg:text-4xl tracking-tight">{hero.title}</div>
                <p className="text-zinc-400 mt-3 max-w-2xl">{hero.desc}</p>
              </div>
              <div className="flex items-center gap-3 text-accent">
                <span className="font-mono-plex uppercase tracking-widest text-xs">Open</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Pillar grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
        {rest.map((m) => {
          const Icon = m.icon;
          return (
            <Link key={m.slug} to={m.href} data-testid={`gmes-pillar-${m.slug}`}
                  className="group border border-border bg-card rounded-sm p-6 hover:border-primary transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-sm bg-primary/10 text-primary grid place-items-center shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg leading-tight">{m.title}</div>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{m.desc}</p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary font-mono-plex uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                    Open <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Custom build CTA */}
      <div className="relative overflow-hidden border-2 border-accent/40 rounded-sm bg-gradient-to-br from-accent/5 via-transparent to-primary/5 p-8 lg:p-10">
        <div className="absolute -top-16 -right-16 w-64 h-64 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 border border-accent/40 bg-accent/10 rounded-sm px-3 py-1.5 mb-4">
              <Wrench className="w-3.5 h-3.5 text-accent" />
              <span className="text-[10px] font-mono-plex uppercase tracking-widest text-accent">◉ Custom build · Free consultation</span>
            </div>
            <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight" data-testid="gmes-custom-title">
              Need a fully-built MES tailored to your floor?
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl">
              Andon lights, PLC integrations, OPC-UA / MQTT gateways, ERP sync (SAP, NetSuite, Odoo, QuickBooks), SPC, traceability, mobile scan-guns — we build it around your machines, your processes, your people.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { icon: ShieldCheck, label: "Multi-tenant + SSO" },
                { icon: Zap, label: "Real-time PLC/MQTT" },
                { icon: Factory, label: "Any process type" },
              ].map(({ icon: I, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5 border border-border rounded-sm px-2.5 py-1 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
                  <I className="w-3 h-3" /> {label}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button onClick={() => setOpenConsult(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white h-12 px-6 text-base" data-testid="gmes-book-consult-btn">
              Book free consultation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <div className="text-[11px] font-mono-plex uppercase tracking-widest text-muted-foreground text-center">
              1 business day · No obligation
            </div>
          </div>
        </div>
      </div>

      {/* Consult dialog */}
      <Dialog open={openConsult} onOpenChange={(v) => (v ? setOpenConsult(true) : closeConsult())}>
        <DialogContent className="sm:max-w-lg rounded-sm">
          {sent ? (
            <div className="py-6 text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <h3 className="font-display font-bold text-xl mt-4" data-testid="gmes-consult-sent">Request received</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Scott will reach out within 1 business day with next steps and available times. In the meantime you can start using the built-in Shop Floor modules above.
              </p>
              <Button onClick={closeConsult} className="mt-5 rounded-sm">Close</Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="overline text-accent">◉ FREE CONSULTATION</div>
                <DialogTitle className="font-display text-2xl font-bold tracking-tight">Custom MES build</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitConsult} className="space-y-3 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Name *</label>
                    <Input value={consult.name} onChange={(e) => setConsult({ ...consult, name: e.target.value })} className="rounded-sm" data-testid="consult-name" required />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Email *</label>
                    <Input type="email" value={consult.email} onChange={(e) => setConsult({ ...consult, email: e.target.value })} className="rounded-sm" data-testid="consult-email" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Company</label>
                    <Input value={consult.company} onChange={(e) => setConsult({ ...consult, company: e.target.value })} className="rounded-sm" />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Your role</label>
                    <Input value={consult.role} onChange={(e) => setConsult({ ...consult, role: e.target.value })} className="rounded-sm" placeholder="Plant manager, ops director…" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Industry</label>
                    <Input value={consult.industry} onChange={(e) => setConsult({ ...consult, industry: e.target.value })} className="rounded-sm" placeholder="Ink/print, food, metals…" />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Machines / lines</label>
                    <Input value={consult.machines_count} onChange={(e) => setConsult({ ...consult, machines_count: e.target.value })} className="rounded-sm" placeholder="e.g. 12 presses" />
                  </div>
                </div>
                <div>
                  <label className="overline text-muted-foreground block mb-1.5">What would success look like?</label>
                  <Textarea rows={4} value={consult.goals} onChange={(e) => setConsult({ ...consult, goals: e.target.value })} className="rounded-sm resize-none" placeholder="What are you trying to improve? Any systems you need to integrate?" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="consult-submit-btn">
                  {submitting ? "Sending…" : "Request consultation"}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  We&rsquo;ll email you at the address above. No spam.
                </p>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
